// The selected device, in full — the third column of the User Access page.
//
// The transfer lists can only show a name and a unique number, because that is
// all the mapping endpoints return. The question an admin actually has when
// looking at someone's access is usually about the DEVICE — whose is it, where
// is it installed, is it alive, what firmware is on it — so the row is fetched
// in full and shown beside the lists rather than in a dialog that would hide
// the lists it belongs to.
//
// ⚠️ EVERY TIMESTAMP HERE IS IST LABELLED AS UTC. The server writes local time
// with a Z on it; parseServerTime applies the same correction deviceStatus uses
// for heartbeats. Printing the raw value shows a device last seen five and a
// half hours in the future.

import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Divider, CircularProgress, Chip,
} from '@mui/material';
import { getDeviceDetail } from '../../../../core/api/api';
import { parseServerTime, firmwareStateOf, CURRENT, STALE } from '../../../../core/devices/firmwareInfo';
import { ageOf, ONLINE_WINDOW_MS } from '../../../../core/devices/deviceStatus';
import { deviceLabel } from '../../../../core/devices/deviceMeta';

const when = (ms) => (ms == null ? '—'
  : new Date(ms).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }));

function Row({ label, value, mono }) {
  if (value == null || value === '') return null;
  return (
    <Box sx={{ display: 'flex', gap: 0.75, py: 0.125, alignItems: 'baseline' }}>
      <Typography
        color="text.secondary"
        sx={{ fontSize: 10.5, width: 62, flexShrink: 0, textAlign: 'right' }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 11.5,
          fontFamily: mono ? 'monospace' : undefined,
          wordBreak: 'break-word',
          minWidth: 0,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

export default function DeviceDetailsPanel({ device }) {
  const uno = device ? String(device.UniqueNumber ?? '') : '';
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!uno) { setRow(null); return undefined; }
    let alive = true;
    setLoading(true);
    // Cleared before the fetch, not after: leaving the previous device's row on
    // screen while the next one loads reads as "these are its details", and on
    // an admin page that is how the wrong device gets changed.
    setRow(null);
    getDeviceDetail(uno)
      .then((res) => {
        if (!alive) return;
        setRow((Array.isArray(res.data) ? res.data[0] : null) || null);
      })
      .catch(() => { if (alive) setRow(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [uno]);

  const shell = (children) => (
    <Paper
      variant="outlined"
      sx={{
        gridArea: 'details',
        p: 0.625, minHeight: 0, display: 'flex', flexDirection: 'column',
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: 12.5, mb: 0.375, flexShrink: 0 }}>
        Device Details
      </Typography>
      <Divider sx={{ mb: 0.5 }} />
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>{children}</Box>
    </Paper>
  );

  if (!uno) {
    return shell(
      <Typography color="text.secondary" sx={{ fontSize: 11 }}>
        Pick a device to see where it is installed, who owns it and what
        firmware it is on.
      </Typography>,
    );
  }

  if (loading) {
    return shell(
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <CircularProgress size={14} />
        <Typography sx={{ fontSize: 11 }}>Loading…</Typography>
      </Box>,
    );
  }

  if (!row) {
    return shell(
      <Typography color="error" sx={{ fontSize: 11 }}>
        {`Could not load ${uno}. It is mapped to this user but has no
        DevicesInstalled row — which is why it may be showing nowhere else.`}
      </Typography>,
    );
  }

  const fw = firmwareStateOf(row);
  const seen = parseServerTime(row.LastUpdatedDateTime);
  // ageOf already carries the server-clock correction, so it takes the RAW
  // stamp — not the one parseServerTime has already corrected.
  const rawSeen = Date.parse(String(row.LastUpdatedDateTime || '').replace(' ', 'T'));
  const online = !Number.isNaN(rawSeen) && ageOf(rawSeen) < ONLINE_WINDOW_MS;

  return shell(
    <>
      {/* NO NAME, LOCATION OR ROOM. All three are already on screen — the name
          is the highlighted row in the panel to the left, and the location and
          room are the panel titles above it. Repeating them spent a third of
          this column restating what the operator just clicked. What is left is
          only what the lists CANNOT show. */}
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mb: 0.5, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: 12, fontFamily: 'monospace', fontWeight: 700 }}>
          {uno}
        </Typography>
        <Chip
          size="small"
          label={online ? 'Online' : 'Offline'}
          color={online ? 'success' : 'default'}
          variant={online ? 'filled' : 'outlined'}
          sx={{ height: 16, fontSize: 9.5 }}
        />
      </Box>

      <Row label="Type" value={`${deviceLabel(row.DeviceID)} · ID ${row.DeviceID}`} />
      <Row label="Product" value={row.DeviceTypeName} />
      <Row label="Channels" value={row.Outputs} />
      <Row label="Owner" value={row.DeviceMasterUserName} />
      <Row label="Install id" value={row.ID} />
      <Divider sx={{ my: 0.5 }} />

      <Row label="Last seen" value={when(seen)} />
      <Row label="Status" value={row.Status} mono />
      <Divider sx={{ my: 0.5 }} />

      {/* The same three facts Update Code decides on, so the two screens can
          never disagree about whether a device is current. */}
      <Row label="Firmware" value={fw.binFile || '—'} mono />
      <Row label="Built" value={when(fw.builtAt)} />
      <Row label="Flashed" value={when(fw.flashedAt)} />
      <Box sx={{ mt: 0.5 }}>
        <Chip
          size="small"
          variant="outlined"
          color={fw.state === CURRENT ? 'success' : (fw.state === STALE ? 'warning' : 'default')}
          label={fw.state === CURRENT ? 'Up to date'
            : (fw.state === STALE ? 'Needs update' : 'Firmware not known')}
          sx={{ height: 17, fontSize: 9.5 }}
        />
      </Box>
    </>,
  );
}
